"use client";

import { CheckIcon, XIcon } from "lucide-react";
import { nanoid } from "nanoid";
import { useState } from "react";
import {
	type UseStoreBuilderContextResult,
	useStoreBuilderContext,
} from "@/ui/admin/store-builder/store-builder-context";
import { BuilderInputField } from "@/ui/builder/builder-input-field";
import { asyncDebounce, debounce } from "@/ui/hooks/use-debounced-value";
import { LogoUpload } from "@/ui/image-upload/og-image";
import { Label } from "@/ui/shadcn/label";
import { TailwindColorPicker } from "@/ui/tailwind-color-picker";
import { CompactButtonGroup } from "../../shared/compact-button-group";
import type { YnsComponentForm } from "../types";
import type { Nav } from "./component";
import type { NavStoreBuilderConfigFactory } from "./config";
import { MenuEditor } from "./menu-editor";
import type { MenuItem } from "./schemas";

const debouncedSaveLinks = asyncDebounce(
	(context: UseStoreBuilderContextResult<typeof Nav>, items: MenuItem[]) => {
		return context.saveSelectedPageSectionField({
			fieldPath: "links",
			value: items,
		});
	},
	300,
);

export const NavForm: YnsComponentForm<typeof NavStoreBuilderConfigFactory> = ({ data, theme, props }) => {
	const context = useStoreBuilderContext<typeof Nav>();

	const [items, setItems] = useState<MenuItem[]>(
		data.links?.map((link) => {
			// legacy links
			if (!("type" in link)) {
				return {
					id: nanoid(),
					type: "custom",
					label: link.label ?? "",
					url: link.href ?? "",
				};
			}
			return link;
		}) ?? [],
	);

	const sizeOptions = [
		{ value: "sm", icon: <span className="text-xs font-medium">S</span>, tooltip: "Small" },
		{ value: "md", icon: <span className="text-xs font-medium">M</span>, tooltip: "Medium" },
		{ value: "lg", icon: <span className="text-xs font-medium">L</span>, tooltip: "Large" },
	];

	const searchBarOptions = [
		{ value: "true", icon: <CheckIcon className="size-3" />, tooltip: "Show search bar" },
		{ value: "false", icon: <XIcon className="size-3" />, tooltip: "Hide search bar" },
	];

	const handleMenuChange = (items: MenuItem[]) => {
		setItems(items);
		debouncedSaveLinks(context, items);
	};

	return (
		<div className="space-y-6">
			{/* Content Section */}
			<div>
				<div className="text-sm font-semibold text-neutral-900 mb-3 pb-2 border-b border-neutral-200">
					Content
				</div>
				<div className="space-y-4">
					<div className="space-y-4">
						<BuilderInputField fieldPath="title" label="Store title" required defaultValue={data.title} />
						<div className="grid gap-1">
							<span className="text-xs text-neutral-600 font-medium">Logo (Optional)</span>
							<div className="max-w-48">
								<LogoUpload
									name="logo"
									image={props.settings?.logo ?? undefined}
									onUpload={(imageUrl, { width, height }) => {
										props.setSettings((settings) => ({ ...settings, logo: { imageUrl, width, height } }));
									}}
									onDelete={() => {
										props.setSettings((settings) => ({ ...settings, logo: null }));
									}}
								/>
							</div>
							<p className="text-xs text-neutral-500">
								{props.settings.logo?.imageUrl
									? "Your logo is displayed instead of the store name. Remove the logo to show text instead."
									: "Upload a logo to replace the text-based store name."}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Layout & Display Section */}
			<div>
				<div className="text-sm font-semibold text-neutral-900 mb-3 pb-2 border-b border-neutral-200">
					Layout & Display
				</div>
				<div className="space-y-4">
					<div className="grid grid-cols-2 gap-2">
						<div className="grid gap-1">
							<span className="text-xs text-neutral-600 font-medium">Logo Size</span>
							<CompactButtonGroup
								options={sizeOptions}
								defaultValue={data?.logoSize ?? "md"}
								onValueChange={(value) => {
									context.saveSelectedPageSectionField({
										fieldPath: "logoSize",
										value: value,
									});
								}}
								className="w-full"
							/>
						</div>

						<div className="grid gap-1">
							<span className="text-xs text-neutral-600 font-medium">Vertical Padding</span>
							<CompactButtonGroup
								options={sizeOptions}
								defaultValue={data?.verticalPadding ?? "sm"}
								onValueChange={(value) => {
									context.saveSelectedPageSectionField({
										fieldPath: "verticalPadding",
										value: value,
									});
								}}
								className="w-full"
							/>
						</div>

						<div className="grid gap-1">
							<span className="text-xs text-neutral-600 font-medium">Show Search Bar</span>
							<CompactButtonGroup
								options={searchBarOptions}
								defaultValue={(data.searchBar?.show ?? true).toString()}
								onValueChange={(value) => {
									context.saveSelectedPageSectionField({
										fieldPath: "searchBar.show",
										value: value === "true",
									});
								}}
								className="w-full"
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Colors Section */}
			<div>
				<div className="text-sm font-semibold text-neutral-900 mb-3 pb-2 border-b border-neutral-200">
					Colors
				</div>
				<div className="space-y-4">
					<div className="grid grid-cols-2 gap-2">
						<Label className="grid gap-1">
							<span className="text-xs text-neutral-600 font-medium">Background</span>
							<TailwindColorPicker
								defaultValue={theme.backgroundColor}
								onValueChange={debounce(
									(value) => {
										context.saveSelectedPageSectionThemeField({
											fieldPath: "backgroundColor",
											value,
										});
									},
									300,
									{ trailing: true },
								)}
							/>
						</Label>

						<Label className="grid gap-1">
							<span className="text-xs text-neutral-600 font-medium">Font</span>
							<TailwindColorPicker
								defaultValue={theme.color}
								onValueChange={debounce(
									(value) => {
										context.saveSelectedPageSectionThemeField({
											fieldPath: "color",
											value,
										});
									},
									300,
									{ trailing: true },
								)}
							/>
						</Label>
					</div>
				</div>
			</div>

			{/* Menu Section */}
			<div>
				<div className="text-sm font-semibold text-neutral-900 mb-3 pb-2 border-b border-neutral-200">
					Menu
				</div>
				<div className="space-y-4">
					<MenuEditor items={items} onChange={handleMenuChange} />
				</div>
			</div>
		</div>
	);
};
